import type { SelectOption } from '@movingimage-evp/mi-ui-component-library';
import type { ChangeEvent } from 'react';

type DefaultIngestSettings =
  | { __typename: 'DefaultRtmpIngestSettings'; authenticationEnabled: boolean }
  | { __typename: 'DefaultSrtIngestSettings'; _?: boolean | null };

import {
  Heading,
  InputDescription,
  Paragraph,
  Radio,
  Select,
  SelectionInputLayout,
  Toggle,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import {
  EventStartVerificationPolicy,
  GetLsproDefaultSettingsDocument,
  type GetLsproDefaultSettingsQuery,
  type GetLsproDefaultSettingsQueryVariables,
  useGetLsproDefaultSettingsQuery,
  useUpdateDefaultTranscodingProfileMutation,
  useUpdateEventStartVerificationPolicyMutation,
  useUpdateLsproDefaultSettingsMutation,
} from '../../../../generated/graphql-manager';
import { useCurrentUser } from '../../../hooks/current-user';
import { useStreamingProfiles } from '../../../hooks/use-streaming-profiles';

import managerStyles from '../../../manager.module.css';
import styles from '../channel-settings.module.css';

export function StreamingPage() {
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();

  const { data } = useGetLsproDefaultSettingsQuery({ variables: { lsproId } });

  const defaultSettings = data?.lsproSettings?.defaultSettings;
  const defaultProfileId = data?.lsproSettings?.transcodingProfileSettings?.defaultProfileId;
  const customProfiles = data?.lsproSettings?.transcodingProfileSettings?.customProfiles;
  const eventStartVerificationPolicy = data?.lsproSettings?.eventStartVerificationPolicy;

  const [updateLsproDefaultSettings] = useUpdateLsproDefaultSettingsMutation();

  const isRtmpProtocol = defaultSettings?.defaultIngestSettings?.__typename === 'DefaultRtmpIngestSettings';
  const defaultIngestAuthEnabled =
    defaultSettings?.defaultIngestSettings?.__typename === 'DefaultRtmpIngestSettings'
      ? defaultSettings.defaultIngestSettings.authenticationEnabled
      : false;

  const handleDefaultSettingsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    updateLsproDefaultSettings({
      variables: {
        input: {
          lsproId,
          [name]: checked,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateLiveStreamProDefaultSettings: {
          __typename: 'UpdateLiveStreamProDefaultSettingsSuccess',
          defaultSettings: {
            __typename: 'LiveStreamProDefaultSettings',
            ...defaultSettings,
            allowTranscodingProfileSelection: defaultSettings?.allowTranscodingProfileSelection ?? false,
            privateEventAsDefault: defaultSettings?.privateEventAsDefault ?? false,
            restrictAttendanceReport: defaultSettings?.restrictAttendanceReport ?? false,
            singleSignOnAsDefault: defaultSettings?.singleSignOnAsDefault ?? false,
            showWatermarkingAsDefault: defaultSettings?.showWatermarkingAsDefault ?? false,
            defaultIngestSettings: defaultSettings?.defaultIngestSettings ?? {
              __typename: 'DefaultRtmpIngestSettings',
              authenticationEnabled: false,
            },
            [name]: checked,
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            defaultSettings: (existingDefaultSettings = {}) => ({
              ...existingDefaultSettings,
              [name]: checked,
            }),
          },
        });
      },
    });
  };

  const handleDefaultIngestSettingsChange = (defaultIngestSettings: DefaultIngestSettings) => {
    const input =
      defaultIngestSettings.__typename === 'DefaultRtmpIngestSettings'
        ? { rtmp: { authenticationEnabled: defaultIngestSettings.authenticationEnabled } }
        : { srt: {} };

    updateLsproDefaultSettings({
      variables: {
        input: {
          lsproId,
          defaultIngestSettings: input,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateLiveStreamProDefaultSettings: {
          __typename: 'UpdateLiveStreamProDefaultSettingsSuccess',
          defaultSettings: {
            __typename: 'LiveStreamProDefaultSettings',
            ...defaultSettings,
            allowTranscodingProfileSelection: defaultSettings?.allowTranscodingProfileSelection ?? false,
            privateEventAsDefault: defaultSettings?.privateEventAsDefault ?? false,
            restrictAttendanceReport: defaultSettings?.restrictAttendanceReport ?? false,
            singleSignOnAsDefault: defaultSettings?.singleSignOnAsDefault ?? false,
            showWatermarkingAsDefault: defaultSettings?.showWatermarkingAsDefault ?? false,
            defaultIngestSettings,
          },
        },
      },
      update: (cache, { data }) => {
        if (data?.updateLiveStreamProDefaultSettings?.__typename !== 'UpdateLiveStreamProDefaultSettingsSuccess')
          return;

        const existingData = cache.readQuery<GetLsproDefaultSettingsQuery, GetLsproDefaultSettingsQueryVariables>({
          query: GetLsproDefaultSettingsDocument,
          variables: { lsproId },
        });

        if (!existingData?.lsproSettings?.defaultSettings) return;

        cache.writeQuery({
          query: GetLsproDefaultSettingsDocument,
          variables: { lsproId },
          data: {
            ...existingData,
            lsproSettings: {
              ...existingData.lsproSettings,
              defaultSettings: {
                ...existingData.lsproSettings.defaultSettings,
                defaultIngestSettings,
              },
            },
          },
        });
      },
    });
  };

  const [updateDefaultTranscodingProfile] = useUpdateDefaultTranscodingProfileMutation();

  const handleStreamingProfileChange = (option: SelectOption) => {
    updateDefaultTranscodingProfile({
      variables: {
        input: {
          lsproId,
          defaultProfileId: option.key,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateDefaultTranscodingProfile: {
          __typename: 'UpdateDefaultTranscodingProfileSuccess',
          defaultProfile: {
            __typename: 'TranscodingProfile',
            id: option.key,
          },
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            transcodingProfileSettings: (existingTranscodingProfileSettings = {}) => ({
              ...existingTranscodingProfileSettings,
              defaultProfileId: option.key,
            }),
          },
        });
      },
    });
  };

  const [updateEventStartVerificationPolicy] = useUpdateEventStartVerificationPolicyMutation();

  const handleEventStartVerificationPolicyChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    updateEventStartVerificationPolicy({
      variables: {
        input: {
          lsproId,
          policy: checked
            ? EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_OR_BACKUP_LANGUAGE_STREAM_REQUIRED
            : EventStartVerificationPolicy.ALL_ACTIVE_LANGUAGE_STREAMS_REQUIRED,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateEventStartVerificationPolicy: {
          __typename: 'UpdateEventStartVerificationPolicySuccess',
          policy: checked
            ? EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_OR_BACKUP_LANGUAGE_STREAM_REQUIRED
            : EventStartVerificationPolicy.ALL_ACTIVE_LANGUAGE_STREAMS_REQUIRED,
        },
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'LiveStreamProSettings', lsproId }),
          fields: {
            eventStartVerificationPolicy() {
              return checked
                ? EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_OR_BACKUP_LANGUAGE_STREAM_REQUIRED
                : EventStartVerificationPolicy.ALL_ACTIVE_LANGUAGE_STREAMS_REQUIRED;
            },
          },
        });
      },
    });
  };

  const streamingProfiles = useStreamingProfiles();
  const chosenStreamingProfile =
    streamingProfiles.find((option) => option.key === defaultProfileId) || streamingProfiles[0];

  return (
    <main className={classNames(managerStyles.main, styles.wrapper)} data-testid="streaming-page">
      <Heading className={managerStyles.grayText}>{t('manager.channelSettings.streaming.heading')}</Heading>

      <SelectionInputLayout
        hintMessage={t('manager.channelSettings.settings.allowEventStartWithoutPrimaryIngest.description')}
        checked={
          eventStartVerificationPolicy ===
          EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_OR_BACKUP_LANGUAGE_STREAM_REQUIRED
        }
        variant="ghost"
        inputElement={
          <Toggle
            data-testid="allow-event-start-without-primary-ingest-toggle"
            name="allowEventStartWithoutPrimaryIngest"
            checked={
              eventStartVerificationPolicy ===
              EventStartVerificationPolicy.ONLY_ACTIVE_PRIMARY_OR_BACKUP_LANGUAGE_STREAM_REQUIRED
            }
            onChange={handleEventStartVerificationPolicyChange}
          >
            {t('manager.channelSettings.settings.allowEventStartWithoutPrimaryIngest.label')}
          </Toggle>
        }
      />

      {customProfiles && customProfiles.length > 0 && (
        <>
          {chosenStreamingProfile && (
            <InputDescription label={t('manager.channelSettings.settings.streamingProfile.label')}>
              <Select
                data-testid="streaming-profile-select"
                optionsIdProperty="key"
                value={chosenStreamingProfile}
                options={streamingProfiles}
                onChange={handleStreamingProfileChange}
              />
            </InputDescription>
          )}

          {defaultSettings?.allowTranscodingProfileSelection !== undefined && (
            <SelectionInputLayout
              hintMessage={t('manager.channelSettings.settings.allowTranscodingProfileSelection.description')}
              checked={defaultSettings?.allowTranscodingProfileSelection}
              variant="ghost"
              inputElement={
                <Toggle
                  data-testid="allow-transcoding-profile-selection-toggle"
                  name="allowTranscodingProfileSelection"
                  checked={defaultSettings?.allowTranscodingProfileSelection}
                  onChange={handleDefaultSettingsChange}
                >
                  {t('manager.channelSettings.settings.allowTranscodingProfileSelection.label')}
                </Toggle>
              }
            />
          )}
        </>
      )}

      {defaultSettings?.defaultIngestSettings && (
        <div className={styles.protocolSection}>
          <Paragraph className={styles.protocolLabel}>
            {t('manager.channelSettings.streaming.defaultProtocol.heading')}
          </Paragraph>

          <div className={styles.protocolOptions}>
            <SelectionInputLayout
              checked={isRtmpProtocol}
              inputElement={
                <Radio
                  data-testid="default-protocol-rtmps-radio"
                  name="defaultProtocol"
                  checked={isRtmpProtocol}
                  onChange={() =>
                    handleDefaultIngestSettingsChange({
                      __typename: 'DefaultRtmpIngestSettings',
                      authenticationEnabled: false,
                    })
                  }
                >
                  {t('manager.channelSettings.streaming.defaultProtocol.rtmps')}
                </Radio>
              }
            />

            {isRtmpProtocol && (
              <SelectionInputLayout
                className={styles.authToggle}
                hintMessage={t('manager.channelSettings.settings.ingestAuthentication.description')}
                inputElement={
                  <Toggle
                    data-testid="default-settings-ingest-authentication-toggle"
                    checked={defaultIngestAuthEnabled}
                    onChange={() =>
                      handleDefaultIngestSettingsChange({
                        __typename: 'DefaultRtmpIngestSettings',
                        authenticationEnabled: !defaultIngestAuthEnabled,
                      })
                    }
                  >
                    {t('manager.channelSettings.settings.ingestAuthentication.label')}
                  </Toggle>
                }
              />
            )}

            <SelectionInputLayout
              checked={!isRtmpProtocol}
              inputElement={
                <Radio
                  data-testid="default-protocol-srt-radio"
                  name="defaultProtocol"
                  checked={!isRtmpProtocol}
                  onChange={() =>
                    handleDefaultIngestSettingsChange({
                      __typename: 'DefaultSrtIngestSettings',
                      _: null,
                    })
                  }
                >
                  {t('manager.channelSettings.streaming.defaultProtocol.srt')}
                </Radio>
              }
            />
          </div>
        </div>
      )}
    </main>
  );
}
